
import { Vue, Prop, Component } from "vue-property-decorator";
import { QuotesConfig } from "@/api/quotes";
import { api } from "@/forms/api";

interface SymbolInfo {
  name: string;
  logo: string;
  symbol: string;
  trend?: string;
}

@Component
export default class QuotesWide extends Vue {
  @Prop({ required: true })
  readonly config!: QuotesConfig & {
    symbols?: SymbolInfo[];
    labels: Record<string, string>;
  };

  collection: { [key: string]: any } = {};
  token: string | undefined;
  websocket: WebSocket | undefined;
  typicalSpreads: { [key: string]: number } = {};
  trends: { [key: string]: string } = {};
  isAlive = false;
  pingPongTimerId: any = 0;

  get symbolsList(): SymbolInfo[] {
    return this.config.symbols || [];
  }

  get pairs(): string {
    if (this.config.symbols) {
      return this.config.symbols.map(s => s.symbol).join(",");
    }
    return this.config.pairs;
  }

  created() {
    this.getTypicalSpreads();
    this.initDummyValues();
    this.fetchTrends();
    this.updateAccessToken().then(() => {
      this.connectToApiToGetData();
    });
  }

  beforeDestroy() {
    if (this.websocket) {
      this.websocket.close();
    }
    clearInterval(this.pingPongTimerId);
  }

  getQuoteValue(symbol: string, field: "bid" | "ask" | "spread") {
    const quote = this.collection[symbol];
    if (!quote || quote[field] === undefined) {
      return "-";
    }
    return quote[field];
  }

  getChangeValue(symbol: string) {
    const quote = this.collection[symbol];
    if (!quote || quote.d_c === undefined) {
      return "--%";
    }
    const val = Number(quote.d_c);
    return (val > 0 ? "+" : "") + val.toFixed(2) + "%";
  }

  getChangeClass(symbol: string) {
    const quote = this.collection[symbol];
    if (!quote || quote.d_c === undefined) {
      return "";
    }
    const val = Number(quote.d_c);
    return val >= 0 ? "qw-table__column--green" : "qw-table__column--red";
  }

  getStateClass(symbol: string) {
    const quote = this.collection[symbol];
    if (!quote || !quote.state) {
      return "";
    }
    return quote.state === "up"
      ? "qw-text--up"
      : quote.state === "down"
      ? "qw-text--down"
      : "";
  }

  // Logic replicated from Quotes.vue
  async updateAccessToken(): Promise<any> {
    try {
      const response = await fetch(this.config.tokenUrl);
      const { token } = await response.json();
      if (token) {
        this.token = token;
        setTimeout(() => this.updateAccessToken(), 300000);
      } else {
        throw new Error("Token has not been provided");
      }
    } catch (e) {
      console.error("Error getting token from server");
    }
  }

  connectToApiToGetData() {
    if (this.token) {
      const websocket = new WebSocket(this.config.ws_api);

      websocket.onopen = () => {
        this.pingPong();
        clearInterval(this.pingPongTimerId);
        this.pingPongTimerId = setInterval(this.pingPong, 10000);

        websocket.send(
          JSON.stringify({
            token: this.token,
            instruments: this.pairs
          })
        );
      };

      websocket.onmessage = (event: MessageEvent) => {
        const json = JSON.parse(event.data);

        if (json.data) {
          this.updateQuotes(json.data);
          return;
        }

        if (json.message && json.message === "pong") {
          this.isAlive = true;
          return;
        }

        websocket.close();
      };

      websocket.onclose = () => {
        setTimeout(() => this.connectToApiToGetData(), 1000);
      };

      websocket.onerror = () => {
        setTimeout(() => websocket.close(), 1000);
      };
      this.websocket = websocket;
    }
  }

  pingPong() {
    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      return;
    }
    this.isAlive = false;
    this.websocket.send(JSON.stringify({ ping: 1 }));
    setTimeout(() => {
      if (!this.isAlive && this.websocket) {
        this.websocket.close();
      }
    }, 2000);
  }

  updateQuotes(quotes: { [key: string]: any }) {
    const currentTime = Math.floor(Date.now() / 1000);
    const updatedCollection = { ...this.collection };

    Object.entries(quotes).forEach(([instrument, quote]: [string, any]) => {
      const slug = this.getInstrumentSlug(instrument);

      const fractionDigits = Math.max(
        this.getNumberOfDecimals(this.typicalSpreads[slug] || 0),
        this.getNumberOfDecimals(quote.bid),
        this.getNumberOfDecimals(quote.ask)
      );

      quote.bid = Number(quote.bid).toFixed(fractionDigits);
      quote.ask = Number(quote.ask).toFixed(fractionDigits);

      if (
        this.typicalSpreads.hasOwnProperty(slug) &&
        currentTime - quote.time > 1800
      ) {
        quote.spread = this.typicalSpreads[slug];
        quote.ask = (Number(quote.spread) + Number(quote.bid)).toFixed(
          fractionDigits
        );
      } else {
        quote.spread = Number(
          (Number(quote.ask) - Number(quote.bid)).toFixed(fractionDigits)
        );
      }

      updatedCollection[instrument] = quote;
    });
    this.collection = updatedCollection;
  }

  getInstrumentSlug(symbol: string) {
    return this.config.instrumentSlugs &&
      this.config.instrumentSlugs.hasOwnProperty(symbol)
      ? this.config.instrumentSlugs[symbol]
      : symbol;
  }

  async getTypicalSpreads() {
    try {
      const spreads = await api.quotes.getTypicalSpreads(this.pairs);
      this.typicalSpreads = spreads.data;
    } catch (e) {}
  }

  initDummyValues() {
    const aPairs = this.pairs.split(",");
    const oDummy = {
      bid: "-",
      ask: "-",
      spread: "0.0",
      d_c: 0,
      state: "",
      time: 0
    };

    aPairs.forEach((pair: string) => {
      const pairName = pair.trim();
      if (pairName && !this.collection[pairName]) {
        Vue.set(this.collection, pairName, oDummy);
      }
    });
  }

  getNumberOfDecimals(number: number | string) {
    if (Math.floor(Number(number)) == number) {
      return 0;
    }
    return number.toString().split(".")[1].length || 0;
  }

  getTrend(symbolData: SymbolInfo): string {
    return this.trends[symbolData.symbol] || symbolData.trend || "";
  }

  async fetchTrends() {
    const symbolsToFetch = this.symbolsList
      .filter(s => !s.trend)
      .map(s => s.symbol)
      .join(",");

    if (!symbolsToFetch) {
      return;
    }

    try {
      const response = await api.quotes.getTrendSvg(symbolsToFetch);
      if (response.data && response.data.trends) {
        Object.entries(response.data.trends).forEach(([symbol, svg]) => {
          if (svg) {
            Vue.set(this.trends, symbol, svg as string);
          }
        });
      }
    } catch (e) {
      console.error(`Error fetching trends for ${symbolsToFetch}`, e);
    }
  }
}
